import React, {useState} from 'react'
import {Modal, Button, Table, Form, Badge, Alert} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {GET_CONTACT_TAGS, CREATE_CONTACT_TAG, UPDATE_CONTACT_TAG, DELETE_CONTACT_TAG} from '../../../gql/queries/tags'
import {ContactTag, IResGetContactTags, IResCreateContactTag, IResUpdateContactTag, IResDeleteContactTag} from '../../../types/tags'
import {toast} from 'react-toastify'
import {useIntl, FormattedMessage} from 'react-intl'
import {RECIPIENT_MESSAGES as M, EM_DASH, REQUIRED_MARK} from '../recipientMessages'
import './TagManager.css'

const getContrastColor = (hex: string): string => {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

interface TagManagerProps {
  show: boolean
  onHide: () => void
  onTagsUpdated?: () => void
}

export const TagManager: React.FC<TagManagerProps> = ({
  show,
  onHide,
  onTagsUpdated,
}) => {
  const intl = useIntl()
  const [editingTag, setEditingTag] = useState<ContactTag | null>(null)
  const [formData, setFormData] = useState({name: '', color: '#3699ff', description: ''})

  const {data, loading, refetch} = useQuery<IResGetContactTags>(GET_CONTACT_TAGS, {
    skip: !show,
  })

  const [createTag, {loading: creating}] = useMutation<IResCreateContactTag>(CREATE_CONTACT_TAG)
  const [updateTag, {loading: updating}] = useMutation<IResUpdateContactTag>(UPDATE_CONTACT_TAG)
  const [deleteTag] = useMutation<IResDeleteContactTag>(DELETE_CONTACT_TAG)

  const tags = data?.contactTags || []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingTag) {
        // Update existing tag
        await updateTag({
          variables: {
            id: editingTag.id,
            name: formData.name,
            color: formData.color,
            description: formData.description,
          },
        })
        toast.success(intl.formatMessage(M.toastTagUpdated))
      } else {
        // Create new tag
        await createTag({
          variables: {
            name: formData.name,
            color: formData.color,
            description: formData.description,
          },
        })
        toast.success(intl.formatMessage(M.toastTagCreated))
      }
      
      // Reset form
      setFormData({name: '', color: '#3699ff', description: ''})
      setEditingTag(null)
      refetch()
      onTagsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorSaveTag))
    }
  }

  const handleEdit = (tag: ContactTag) => {
    setEditingTag(tag)
    setFormData({
      name: tag.name,
      color: tag.color,
      description: tag.description || '',
    })
  }

  const handleDelete = async (tagId: string) => {
    if (!window.confirm(intl.formatMessage(M.confirmDeleteTag))) {
      return
    }

    try {
      await deleteTag({variables: {id: tagId}})
      toast.success(intl.formatMessage(M.toastTagDeleted))
      refetch()
      onTagsUpdated?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorDeleteTag))
    }
  }

  const handleCancel = () => {
    setEditingTag(null)
    setFormData({name: '', color: '#3699ff', description: ''})
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage {...M.manageTags} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Create/Edit Form */}
        <div className='tag-form mb-4'>
          <Form onSubmit={handleSubmit}>
            <div className='d-flex align-items-end gap-2 flex-wrap'>
              <Form.Group style={{flex: '2 1 180px'}}>
                <Form.Label className='mb-1 small'>
                  <FormattedMessage {...M.tagName} /> {REQUIRED_MARK}
                </Form.Label>
                <Form.Control
                  type='text'
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={intl.formatMessage(M.tagNamePlaceholder)}
                />
              </Form.Group>
              <Form.Group style={{flex: '0 0 auto'}}>
                <Form.Label className='mb-1 small'><FormattedMessage {...M.color} /></Form.Label>
                <Form.Control
                  type='color'
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  style={{width: '44px', height: '38px', padding: '2px'}}
                  title={formData.color}
                />
              </Form.Group>
              <Form.Group style={{flex: '2 1 180px'}}>
                <Form.Label className='mb-1 small'>
                  <FormattedMessage {...M.description} />
                </Form.Label>
                <Form.Control
                  type='text'
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={intl.formatMessage(M.optionalPlaceholder)}
                />
              </Form.Group>
              <div className='d-flex gap-2'>
                <Button type='submit' variant='primary' disabled={creating || updating}>
                  {intl.formatMessage(editingTag ? M.updateTag : M.createTag)}
                </Button>
                {editingTag && (
                  <Button type='button' variant='secondary' onClick={handleCancel}>
                    <FormattedMessage {...M.cancel} />
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </div>

        {/* Tags List */}
        <div className='tags-list'>
          <h6 className='mb-3'>
            <FormattedMessage {...M.existingTags} values={{count: tags.length}} />
          </h6>
          {loading ? (
            <div className='text-center py-4'>
              <div className='spinner-border spinner-border-sm' />
            </div>
          ) : tags.length === 0 ? (
            <Alert variant='info'>
              <FormattedMessage {...M.tagsEmpty} />
            </Alert>
          ) : (
            <Table striped hover size='sm'>
              <thead>
                <tr>
                  <th style={{width: '40px'}}><FormattedMessage {...M.color} /></th>
                  <th><FormattedMessage {...M.name} /></th>
                  <th><FormattedMessage {...M.description} /></th>
                  <th style={{width: '100px'}}><FormattedMessage {...M.contactsColumn} /></th>
                  <th style={{width: '120px'}}><FormattedMessage {...M.actions} /></th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => (
                  <tr key={tag.id}>
                    <td>
                      <span
                        className='badge rounded-pill px-2'
                        style={{
                          backgroundColor: tag.color,
                          color: getContrastColor(tag.color),
                        }}
                      >
                        {tag.name}
                      </span>
                    </td>
                    <td>
                      <strong>{tag.name}</strong>
                    </td>
                    <td className='text-muted small'>{tag.description || EM_DASH}</td>
                    <td>
                      <Badge bg='secondary'>{tag.contactCount || 0}</Badge>
                    </td>
                    <td>
                      <div className='d-flex gap-1'>
                        <Button
                          size='sm'
                          variant='outline-primary'
                          onClick={() => handleEdit(tag)}
                        >
                          <FormattedMessage {...M.edit} />
                        </Button>
                        <Button
                          size='sm'
                          variant='outline-danger'
                          onClick={() => handleDelete(tag.id)}
                        >
                          <FormattedMessage {...M.delete} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          <FormattedMessage {...M.close} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
